<template>
  <Transition name="fade" @after-leave="handleAfterLeave">
    <div v-if="show" class="video-call-modal">
      <!-- Background with user cover image -->
      <div class="background-cover">
        <img :src="userInfo?.avatar" alt="background" />
        <div class="overlay" />
      </div>

      <!-- User info section -->
      <div class="user-section">
        <!-- User avatar -->
        <div class="user-avatar">
          <img :src="userInfo?.avatar" alt="avatar" />
        </div>

        <!-- User name -->
        <h3 class="user-name">{{ userInfo?.nickname }}</h3>

        <!-- Country flag -->
        <p class="user-country">{{ userInfo?.nationFlag }}{{ userInfo?.nationName }}</p>
      </div>

      <div class="bottom-section">
        <!-- Video chat cost -->
        <div class="cost-section">
          {{ $t('videoCall.call.costLabel') }}
          <img :src="costInfo.icon" alt="cost icon" class="cost-icon" />
          {{ $t('common.pricePerMin', { price: costInfo.price }) }}
        </div>

        <!-- Waiting text -->
        <p class="waiting-text">{{ $t('videoCall.call.waiting') }}</p>

        <!-- Hang up button -->
        <button class="hang-up-btn" @click="handleHangUp">
          <img src="@/assets/images/icons/phone-hang-up.svg" alt="hang up" class="phone-icon" />
        </button>

        <!-- Auto cancel timer -->
        <p v-if="autoCloseSeconds === 0" class="timer-text">{{ $t('common.waiting') }}</p>
        <p v-else class="timer-text">{{ $t('videoCall.call.autoCancel', { seconds: remaining }) }}</p>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { watch, onBeforeUnmount, computed } from 'vue'
import { useCountdown } from '@vueuse/core'
import { getVideoCostInfo } from '@/utils'
import { getAsset } from '@/utils/assets'

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  userInfo: {
    type: Object,
    required: false,
    default: () => ({
      memberId: 0,
      id: 0,
      nickname: '',
      avatar: '',
      nationFlag: '',
      nationName: '',
    }),
  },
  free: {
    type: Boolean,
    default: false,
  },
  price: {
    type: Number,
    default: 0,
  },
  cardType: {
    type: String,
    default: '',
  },
  autoCloseSeconds: {
    type: Number,
    default: 30,
  },
})

const emit = defineEmits(['update:show', 'close', 'closed', 'hangup', 'timeout'])
// 使用 vueuse 的 useCountdown
const { remaining, start, stop } = useCountdown(props.autoCloseSeconds, {
  immediate: false,
  onComplete: () => {
    emit('timeout')
  }
})

const costInfo = computed(() => {
  if (props.free) {
    return {
      icon: props.cardType === getVideoCostInfo.CARD_TYPE.VIDEO_CARD
        ? getAsset('@/assets/images/icons/video-card.svg')
        : getAsset('@/assets/images/icons/exp-card_video.svg'),
      price: 1,
    }
  }

  return {
    icon: getAsset('@/assets/images/icons/diamond.svg'),
    price: props.price,
  }
})

// Watch props.show to sync with visible
watch(
  () => props.show,
  (newVal) => {
    if (newVal) {
      props.autoCloseSeconds > 0 && start(props.autoCloseSeconds)
    } else {
      emit('close')
      stop()
    }
  },
  { immediate: true }
)

function handleHangUp() {
  stop()
  emit('hangup')
}

function handleAfterLeave() {
  emit('closed')
}

onBeforeUnmount(() => {
  stop()
})
</script>

<style scoped lang="less">
// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.video-call-modal {
  position: fixed;
  // inset: 0; // top: 0; left: 0; right: 0; bottom: 0; 的缩写
  width: 100%;
  height: 100%;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  z-index: 9999;

  // Background cover with overlay - 使用用户头像作为全屏背景
  .background-cover {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .overlay {
      position: absolute;
      inset: 0;
      background: rgba(0, 0, 0, 0.5);
    }
  }

  // User section
  .user-section {
    margin-top: 68px;
    display: flex;
    flex-direction: column;
    align-items: center;
    z-index: 2;

    .user-avatar {
      width: 120px;
      height: 120px;
      border-radius: 50%;
      overflow: hidden;
      margin-bottom: 12px;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    .user-name {
      width: 250px;
      font-family: 'PingFang SC', sans-serif;
      font-size: 16px;
      font-weight: 500;
      color: #ffffff;
      line-height: normal;
      letter-spacing: 0.2286px;
      margin-bottom: 4px;
      text-align: center;
      .ellipsis();
    }

    .user-country {
      font-family: 'PingFang SC', sans-serif;
      font-size: 14px;
      color: #ffffff;
      line-height: normal;
      text-align: center;
    }
  }

  .bottom-section {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 93px;

    // Cost section
    .cost-section {
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 4px;
      z-index: 2;
      font-family: 'PingFang SC', sans-serif;
      font-size: 18px;
      color: #ffffff;
      line-height: normal;

      .cost-icon {
        width: 24px;
        height: 24px;
      }
    }

    // Waiting text
    .waiting-text {
      margin-bottom: 24px;
      font-family: 'PingFang SC', sans-serif;
      font-size: 14px;
      color: #ffffff;
      line-height: normal;
      text-align: center;
      z-index: 2;
    }

    // Hang up button
    .hang-up-btn {
      width: 56px;
      height: 56px;
      margin-bottom: 13px;
      cursor: pointer;
      z-index: 2;
      background: transparent;
      border: none;
      padding: 0;

      &:active {
        opacity: 0.8;
      }

      .phone-icon {
        width: 100%;
        height: 100%;
      }
    }

    // Timer text
    .timer-text {
      font-family: 'PingFang SC', sans-serif;
      font-size: 12px;
      color: rgba(255, 255, 255, 0.8);
      line-height: normal;
      text-align: center;
      z-index: 2;
    }
  }
}
</style>
